import { create } from 'zustand';
import type { JournalSummary, JournalView, ContinuationEvent, DashboardLayout, WidgetConfig } from '../types';
import { journalApi, type StreamHandle } from '../services/api';

interface JournalStore {
  journals: JournalSummary[];
  activeJournal: JournalView | null;
  liveEvents: ContinuationEvent[];
  eventSource: StreamHandle | null;
  layout: DashboardLayout;
  mode: 'flight' | 'tower' | 'studio';

  loadJournals: () => Promise<void>;
  loadJournal: (rootId: string) => Promise<void>;
  startStream: (rootId: string) => void;
  stopStream: () => void;
  setMode: (mode: 'flight' | 'tower' | 'studio') => void;
  updateLayout: (widgets: WidgetConfig[]) => void;
  addWidget: (widget: WidgetConfig) => void;
  removeWidget: (widgetId: string) => void;
}

const DEFAULT_FLIGHT_LAYOUT: DashboardLayout = {
  id: 'flight-default',
  name: 'Flightmode',
  widgets: [
    { id: 'w-goal', type: 'goal-frame', title: 'Goal Frame', x: 0, y: 0, w: 4, h: 3, minW: 3, minH: 2 },
    { id: 'w-findings', type: 'findings-stream', title: 'Findings', x: 4, y: 0, w: 5, h: 6, minW: 3, minH: 3 },
    { id: 'w-budget', type: 'budget-gauge', title: 'Budget', x: 9, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
    { id: 'w-events', type: 'event-log', title: 'Event Log', x: 0, y: 3, w: 4, h: 5, minW: 3, minH: 3 },
    { id: 'w-draft', type: 'draft-viewer', title: 'Working Draft', x: 4, y: 6, w: 5, h: 4, minW: 3, minH: 2 },
    { id: 'w-status', type: 'status-board', title: 'Status', x: 9, y: 3, w: 3, h: 3, minW: 2, minH: 2 },
    { id: 'w-annotate', type: 'annotation-input', title: 'Annotate', x: 9, y: 6, w: 3, h: 4, minW: 2, minH: 2 },
    { id: 'w-copilot', type: 'copilot-panel', title: 'Copilot', x: 0, y: 10, w: 12, h: 5, minW: 3, minH: 4 },
  ],
};

const DEFAULT_TOWER_LAYOUT: DashboardLayout = {
  id: 'tower-default',
  name: 'Tower',
  widgets: [
    { id: 'w-lineage', type: 'lineage-tree', title: 'Lineage Tree', x: 0, y: 0, w: 6, h: 6, minW: 4, minH: 4 },
    { id: 'w-status', type: 'status-board', title: 'Status Board', x: 6, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
    { id: 'w-budget', type: 'budget-gauge', title: 'Budget Overview', x: 9, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
    { id: 'w-wake', type: 'wake-monitor', title: 'Wake Conditions', x: 6, y: 3, w: 3, h: 3, minW: 2, minH: 2 },
    { id: 'w-cost', type: 'cost-tracker', title: 'Cost Tracker', x: 9, y: 3, w: 3, h: 3, minW: 2, minH: 2 },
    { id: 'w-events', type: 'event-log', title: 'Event Log', x: 0, y: 6, w: 6, h: 4, minW: 3, minH: 3 },
    { id: 'w-findings', type: 'findings-stream', title: 'Findings', x: 6, y: 6, w: 6, h: 4, minW: 3, minH: 3 },
    { id: 'w-copilot', type: 'copilot-panel', title: 'Copilot', x: 0, y: 10, w: 12, h: 5, minW: 3, minH: 4 },
  ],
};

const DEFAULT_STUDIO_LAYOUT: DashboardLayout = {
  id: 'studio-default',
  name: 'Studio',
  widgets: [
    { id: 'w-mcp-browser', type: 'mcp-server-browser', title: 'MCP Sources', x: 0, y: 0, w: 3, h: 5, minW: 2, minH: 3 },
    { id: 'w-sql-editor', type: 'sql-editor', title: 'SQL Editor', x: 3, y: 0, w: 6, h: 5, minW: 4, minH: 3 },
    { id: 'w-timeline', type: 'artifact-timeline', title: 'Version History', x: 9, y: 0, w: 3, h: 5, minW: 3, minH: 3 },
    { id: 'w-env', type: 'env-switcher', title: 'Env Switcher', x: 0, y: 5, w: 2, h: 4, minW: 2, minH: 3 },
    { id: 'w-schema', type: 'data-source-explorer', title: 'Schema Explorer', x: 2, y: 5, w: 2, h: 4, minW: 2, minH: 3 },
    { id: 'w-results', type: 'query-results', title: 'Query Results', x: 4, y: 5, w: 3, h: 4, minW: 3, minH: 3 },
    { id: 'w-copilot', type: 'copilot-panel', title: 'Studio Copilot', x: 7, y: 5, w: 2, h: 4, minW: 2, minH: 3 },
    { id: 'w-events', type: 'event-log', title: 'Event Log', x: 9, y: 5, w: 3, h: 4, minW: 3, minH: 3 },
    { id: 'w-context', type: 'active-context', title: 'Active Context', x: 0, y: 9, w: 5, h: 3, minW: 3, minH: 3 },
  ],
};

export const useJournalStore = create<JournalStore>((set, get) => ({
  journals: [],
  activeJournal: null,
  liveEvents: [],
  eventSource: null,
  layout: DEFAULT_FLIGHT_LAYOUT,
  mode: 'flight',

  loadJournals: async () => {
    const journals = await journalApi.list();
    set({ journals });
  },

  loadJournal: async (rootId: string) => {
    const journal = await journalApi.get(rootId);
    set({ activeJournal: journal, liveEvents: journal.recentEvents });
  },

  startStream: (rootId: string) => {
    get().stopStream();
    const es = journalApi.stream(rootId, (evt) => {
      set((state) => ({ liveEvents: [...state.liveEvents.slice(-200), evt] }));
    });
    set({ eventSource: es });
  },

  stopStream: () => {
    const es = get().eventSource;
    if (es) { es.close(); set({ eventSource: null }); }
  },

  setMode: (mode) => {
    const layout = mode === 'studio'
      ? DEFAULT_STUDIO_LAYOUT
      : mode === 'tower'
        ? DEFAULT_TOWER_LAYOUT
        : DEFAULT_FLIGHT_LAYOUT;
    set({ mode, layout });
  },

  updateLayout: (widgets) => {
    set((state) => ({ layout: { ...state.layout, widgets } }));
  },

  addWidget: (widget) => {
    set((state) => ({ layout: { ...state.layout, widgets: [...state.layout.widgets, widget] } }));
  },

  removeWidget: (widgetId) => {
    set((state) => ({
      layout: { ...state.layout, widgets: state.layout.widgets.filter(w => w.id !== widgetId) },
    }));
  },
}));
